// Every outgoing frame, in one place.
//
// Mirrors `NakamaHelpers.sendData` — the Flutter client's single send point.
// The same discipline applies here: nothing else in this codebase may touch
// the socket's `sendMatchState`, so that adding a send means adding a function
// here, where its opCode and payload shape are visible next to all the others.
//
// The client only ever sends SEVEN of the 27 opCodes. Everything else is
// server -> client, which is what "server-authoritative" means in practice:
// there is no client-side scoring, no client-side turn logic, and no way for a
// renderer to assert anything about the match beyond these seven statements.

import { OpCode } from './opcodes.generated';

/** The transport this module needs. Narrow on purpose — see gateway.ts. */
export interface MatchSender {
  send(opCode: number, payload: unknown): void;
}

/** opCode 13 — "I have the state and I'm ready." Unblocks allowGameStart. */
export function acknowledgeJoin(tx: MatchSender): void {
  tx.send(OpCode.acknowledgeJoin, {});
}

/** opCode 8 — host only: begin the match. */
export function startGame(tx: MatchSender): void {
  tx.send(OpCode.startGame, {});
}

/** opCode 4 — this client was asked to pick, and picked. */
export function selectAlphabet(tx: MatchSender, alphabet: string): void {
  tx.send(OpCode.alphabetSelected, { alphabet });
}

/**
 * opCode 6 — submit this round's answers, as `{category: answer}`.
 *
 * Sent as a BARE MAP with no round number, exactly as the Flutter client does
 * (`jsonEncode(answers)`), because the server scores against the round it
 * currently has open. Adding a round field here would be a protocol change,
 * not a clarification.
 *
 * Safe to call more than once per round: round end, submit-on-pause, and the
 * reconnect resend all route through here, and the server's late-submission
 * window accepts a resend inside the rejoin grace.
 */
export function submitAnswers(tx: MatchSender, answers: Readonly<Record<string, string>>): void {
  tx.send(OpCode.submitAnswers, answers);
}

/** opCode 9 — end the current round early for everyone. */
export function stopRound(tx: MatchSender): void {
  tx.send(OpCode.stop, {});
}

/**
 * opCode 11 — "I have seen the results for this round."
 *
 * This is what drives the server's fast-advance: once every ONLINE HUMAN has
 * acknowledged, the server brings the next-round deadline forward and
 * republishes it as opCode 27. Failing to send this does not break the match
 * (the 45s hard cap still fires) but it makes every other player wait out the
 * cap, so it is not optional in practice.
 */
export function acknowledgeResults(tx: MatchSender, userId: string, round: number): void {
  tx.send(OpCode.acknowledgeResults, { userId, round });
}

/** opCode 17 — host only: skip the results screen to the next round. */
export function skipResults(tx: MatchSender, round: number): void {
  tx.send(OpCode.skipResults, { round });
}

/** opCode 16 — leaving deliberately, as opposed to dropping. */
export function quitRequest(tx: MatchSender): void {
  tx.send(OpCode.quitRequest, {});
}
